import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, ChevronDown } from "lucide-react";
import axios from "axios";
import { API } from "../App";

/**
 * PublicFAQSection — read-only accordion rendered on prelaunch/launch pages.
 *
 * Fetches the page-scoped FAQ list from /api/faqs/public?page=… so the
 * content is fully controlled from the founder's /admin/faq panel without
 * any redeploy. Matches the existing prelaunch design tokens:
 *   • Outfit headings, plum (#6B4E8A) primary, slate body text
 *   • Cream surfaces (#FAF7F2), soft border (#E5DFEC)
 *   • Single accordion expand at a time
 *
 * Renders nothing if the founder has hidden all questions — the section
 * cleanly disappears from the page until they re-activate one.
 */
const PublicFAQSection = ({ page = "prelaunch" }) => {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/faqs/public?page=${encodeURIComponent(page)}`);
        if (!cancelled) setItems(res.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  if (!loaded || items.length === 0) return null;

  return (
    <section className="relative z-10 px-6 py-16 sm:py-20" data-testid="public-faq-section">
      <div className="max-w-3xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/80 border border-[#E5DFEC] rounded-full text-xs font-medium text-[#6B4E8A] mb-4 backdrop-blur-sm">
            <HelpCircle className="w-3.5 h-3.5" />
            FAQ
          </div>
          <h2
            className="text-2xl sm:text-3xl font-light text-[#2A2A2A] mb-3"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Frequently asked questions
          </h2>
          <p className="text-sm sm:text-base text-[#475569] max-w-xl mx-auto">
            Quick answers to the things most people ask before joining.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3" data-testid="faq-list">
          {items.map((item) => {
            const isOpen = openId === item.faq_id;
            return (
              <motion.div
                key={item.faq_id}
                layout
                className={`bg-white border rounded-2xl overflow-hidden transition-colors ${
                  isOpen ? "border-[#6B4E8A]/40 shadow-sm" : "border-[#E5DFEC]"
                }`}
                data-testid={`faq-item-${item.faq_id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : item.faq_id)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 text-left hover:bg-[#FAF7F2]/60 transition-colors"
                  data-testid={`faq-toggle-${item.faq_id}`}
                >
                  <span
                    className="text-base sm:text-lg font-medium text-[#6B4E8A]"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {item.question}
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="shrink-0 w-7 h-7 rounded-full bg-[#F0ECF7] flex items-center justify-center text-[#6B4E8A]"
                    aria-hidden="true"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-base text-[#475569] leading-relaxed whitespace-pre-wrap">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PublicFAQSection;
